import { CameraOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Avatar, theme } from 'antd';
import React, { FC, useState } from 'react';

import { EmptyImage } from '../Icon';

interface UploadAvatarProps {
  url?: string;
  onChange?: (file: File) => void;
}

const UploadWrapper = styled.div`
  display: inline-block;
  position: relative;
`;

const UploadButton = styled.div`
  position: absolute;
  cursor: pointer;
  width: 40px;
  height: 40px;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow:
    rgba(60, 64, 67, 0.3) 0px 1px 2px 0px,
    rgba(60, 64, 67, 0.15) 0px 2px 6px 2px;
  background-color: #fff;
`;

/**
 * Avatar upload component with:
 * - Image preview capabilities
 * - File selection dialog
 * - Base64 conversion for preview
 */
export const UploadAvatar: FC<UploadAvatarProps> = ({ url, onChange }) => {
  const [previewImage, setPreviewImage] = useState<string | undefined>(
    undefined
  );

  const {
    token: { colorPrimary },
  } = theme.useToken();

  /**
   * Handles file selection and conversion
   * @param event - File input change event
   */
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const base64 = await getBase64(selectedFile);
      setPreviewImage(base64);
      onChange?.(selectedFile);
    }
  };

  /**
   * Converts a File object to base64 string
   * @param file - The file to convert
   * @returns Promise resolving to base64 string
   */
  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  return (
    <UploadWrapper>
      <Avatar
        size={128}
        shape="circle"
        src={previewImage ?? url}
        icon={<EmptyImage />}
      />
      <input
        id="file-input-upload-avatar"
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <UploadButton
        onClick={() =>
          document.getElementById('file-input-upload-avatar')?.click()
        }
      >
        <CameraOutlined style={{ color: colorPrimary }} className="text-xl" />
      </UploadButton>
    </UploadWrapper>
  );
};
